/* SpellingBlitzGame — puzzle generation and game logic engine
The game engine class SpellingBlitzGame. Handles seeded puzzle generation (pangram-based 7-letter sets), 
validation, scoring rules (pangram bonus), submit logic, shuffling, reset, and computing max possible score
*/

class SpellingBlitzGame {
  constructor({ seed = null, dictionary = [] } = {}) {
    this.seed = seed || SpellingBlitzGame.generateSeed();
    this.dictionary = dictionary;
    this.score = 0;
    this.foundWords = [];
    this.letters = [];
    this.centerLetter = '';
    this.outerLetters = [];
    this.validWords = [];
    this.pangrams = [];
    this.puzzleReady = false;
  }

  static generateSeed() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // Seeded random generator
  _makeRng(seed) {
    let s = typeof seed === 'string' ? this._hashSeed(seed) : seed;
    return () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
  }

  _hashSeed(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
  }

  /**
   * Generate the puzzle from the dictionary and seed.
   * Must be called after dictionary is loaded.
   */
  generatePuzzle() {
    if (!this.dictionary || this.dictionary.length === 0) return;

    const rng = this._makeRng(this.seed);

    // Build candidate puzzles: find words with exactly 7 unique letters (pangrams)
    const pangramCandidates = [];
    for (const word of this.dictionary) {
      if (word.length < 7) continue;
      const unique = new Set(word.split(''));
      if (unique.size === 7) {
        pangramCandidates.push({ word, letters: [...unique] });
      }
    }

    if (pangramCandidates.length === 0) return;

    // Pick a pangram using seeded random, ensuring at least 30 valid words
    let startIndex = Math.floor(rng() * pangramCandidates.length);
    let attempts = 0;

    while (attempts < pangramCandidates.length) {
      const idx = (startIndex + attempts) % pangramCandidates.length;
      const chosen = pangramCandidates[idx]; // eslint-disable-line security/detect-object-injection -- array access with numeric loop index
      const letterSet = [...chosen.letters]; // copy so we can shuffle

      // Shuffle letters deterministically
      for (let i = letterSet.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [letterSet[i], letterSet[j]] = [letterSet[j], letterSet[i]]; // eslint-disable-line security/detect-object-injection -- Fisher-Yates shuffle with numeric loop indices
      }

      // Pick center letter (first after shuffle)
      this.centerLetter = letterSet[0];
      this.outerLetters = letterSet.slice(1);
      this.letters = letterSet;

      // Build valid word list
      const letterSetObj = new Set(letterSet);
      this.validWords = [];
      this.pangrams = [];

      for (const word of this.dictionary) {
        if (word.length < 4) continue;
        if (!word.includes(this.centerLetter)) continue;
        let valid = true;
        for (const ch of word) {
          if (!letterSetObj.has(ch)) {
            valid = false;
            break;
          }
        }
        if (valid) {
          this.validWords.push(word);
          const unique = new Set(word.split(''));
          if (unique.size === 7) {
            this.pangrams.push(word);
          }
        }
      }
// THIS SECTION WAS ADDED TO ENSURE THAT THE PUZZLE HAS ENOUGH VALID WORDS

      if (this.validWords.length >= 40) {
        break; // Found a valid puzzle with enough words
      }

      attempts++;
    }

    this.puzzleReady = true;
  }

  /**
   * Score a word: 4-letter words = 1 pt, longer = 1 pt per letter, pangram = +7 bonus.
   */
  static scoreWord(word, isPangram) {
    if (word.length === 4) return 1;
    let pts = word.length;
    if (isPangram) pts += 7;
    return pts;
  }

  /**
   * Attempt to submit a word. Returns { valid, reason, points } 
   */
  submitWord(word) {
    const w = word.toLowerCase().trim();

    if (w.length < 4) {
      return { valid: false, reason: 'Too short', points: 0 };
    }
    if (!w.includes(this.centerLetter)) {
      return { valid: false, reason: 'Missing center letter', points: 0 };
    }
    const letterSet = new Set(this.letters);
    for (const ch of w) {
      if (!letterSet.has(ch)) {
        return { valid: false, reason: 'Invalid letter', points: 0 };
      }
    }
    if (this.foundWords.includes(w)) {
      return { valid: false, reason: 'Already found', points: 0 };
    }
    if (!this.validWords.includes(w)) {
      return { valid: false, reason: 'Not in word list', points: 0 };
    }

    const isPangram = this.pangrams.includes(w);
    const points = SpellingBlitzGame.scoreWord(w, isPangram);
    this.foundWords.push(w);
    this.score += points;

    return { valid: true, reason: isPangram ? 'Pangram!' : 'Nice!', points, isPangram };
  }

  shuffleOuterLetters() {
    const arr = [...this.outerLetters];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]]; // eslint-disable-line security/detect-object-injection -- Fisher-Yates shuffle with numeric loop indices
    }
    this.outerLetters = arr;
  }

  reset(seed = null) {
    this.seed = seed || SpellingBlitzGame.generateSeed();
    this.score = 0;
    this.foundWords = [];
    this.letters = [];
    this.centerLetter = '';
    this.outerLetters = [];
    this.validWords = [];
    this.pangrams = [];
    this.puzzleReady = false;
  }

  getMaxPossibleScore() {
    let total = 0;
    for (const word of this.validWords) {
      const isPangram = this.pangrams.includes(word);
      total += SpellingBlitzGame.scoreWord(word, isPangram);
    }
    return total;
  }
}

export default SpellingBlitzGame;
